import React, { useState, useEffect, useRef } from 'react';
import { Send, Plus, Trash2, Archive, Menu, X, ArrowLeft, MessageSquare, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from './ui/card';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Chat = () => {
  const { user, logout, idToken, isAuthenticated } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [demoQuestionCount, setDemoQuestionCount] = useState(0);
  const [showDemoLimit, setShowDemoLimit] = useState(false);
  const messagesEndRef = useRef(null);
  
  const DEMO_QUESTION_LIMIT = 2;

  // Auto scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const response = await axios.get(`${API}/chat/sessions`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      if (response.data.success) {
        setSessions(response.data.sessions);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/chat/sessions/${sessionId}`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      if (response.data.success) {
        setCurrentSession(response.data.session);
        setMessages(response.data.session.messages || []);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Failed to load conversation');
    }
  };

  const startNewConversation = () => {
    setCurrentSession(null);
    setMessages([]);
    setInputMessage('');
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    // Check demo limit for non-authenticated users
    if (!isAuthenticated && demoQuestionCount >= DEMO_QUESTION_LIMIT) {
      setShowDemoLimit(true);
      return;
    }

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Add user message to UI immediately
    const tempUserMsg = {
      id: Date.now().toString(),
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);
    setIsLoading(true);

    try {
      // Demo mode for non-authenticated users
      if (!isAuthenticated) {
        setDemoQuestionCount(prev => prev + 1);
        
        // Simulate AI thinking delay
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Demo response that's helpful but encourages contact
        const demoResponse = getDemoResponse(demoQuestionCount + 1);
        
        const assistantMsg = {
          id: Date.now().toString() + '1',
          role: 'assistant',
          content: demoResponse,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, assistantMsg]);
        
        // Show limit message after 2nd question
        if (demoQuestionCount + 1 >= DEMO_QUESTION_LIMIT) {
          setTimeout(() => setShowDemoLimit(true), 500);
        }
      } else {
        // Authenticated user - full functionality
        const response = await axios.post(`${API}/chat/message`, {
          message: userMessage,
          session_id: currentSession?.id
        }, {
          headers: {
            Authorization: `Bearer ${idToken}`
          }
        });

        if (response.data.success) {
          // Add assistant response
          const assistantMsg = {
            id: Date.now().toString() + '1',
            role: 'assistant',
            content: response.data.response,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, assistantMsg]);

          // Update current session
          if (!currentSession) {
            // New session created
            await loadSessions();
            await loadSession(response.data.session_id);
          } else {
            // Existing session - reload to get updated messages
            await loadSession(currentSession.id);
            await loadSessions(); // Refresh sidebar
          }
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
      // Remove the temporary user message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempUserMsg.id));
    } finally {
      setIsLoading(false);
    }
  };
  
  const getDemoResponse = (questionNumber) => {
    const responses = [
      "That's a great question! Based on your needs, there are several architectural approaches we could explore. At Westbay Group, we specialize in designing scalable, secure cloud solutions tailored to your specific requirements.\n\n**To provide you with detailed recommendations and a comprehensive strategy, I'd encourage you to:**\n\n• **Contact us at hello@westbay.io** for a personalized consultation\n• **Sign in** to access our full AI-powered architecture guidance\n\nOur team has helped numerous clients solve similar challenges with proven, production-ready solutions.",
      
      "Excellent follow-up! This requires a more detailed analysis of your specific infrastructure, compliance requirements, and scalability goals.\n\n**You've reached the demo limit (2 questions). To continue our conversation and get actionable solutions:**\n\n✅ **Sign in** to unlock unlimited AI-powered guidance\n✅ **Email us at hello@westbay.io** to discuss your project\n✅ **Schedule a consultation** for personalized recommendations\n\nAs a veteran-owned firm, we're committed to delivering enterprise-grade solutions with the precision and reliability you'd expect from military standards. Let's discuss how we can help you succeed."
    ];
    
    return responses[questionNumber - 1] || responses[1];
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;

    try {
      await axios.delete(`${API}/chat/sessions/${sessionId}`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (currentSession?.id === sessionId) {
        startNewConversation();
      }
      toast.success('Conversation deleted');
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  return (
    <div className="chat-container">
      {/* Sidebar */}
      <div className={`chat-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <button onClick={() => window.location.href = '/'} className="back-button">
            <ArrowLeft size={20} />
            <span>Home</span>
          </button>
          <button onClick={startNewConversation} className="new-chat-button">
            <Plus size={20} />
            <span>New Chat</span>
          </button>
        </div>

        <div className="sessions-list">
          {sessions.length === 0 ? (
            <div className="no-sessions">
              <MessageSquare size={48} />
              <p>No conversations yet</p>
              <p className="small-text">Start a new chat to begin</p>
            </div>
          ) : (
            sessions.map(session => (
              <div
                key={session.id}
                className={`session-item ${currentSession?.id === session.id ? 'active' : ''}`}
                onClick={() => loadSession(session.id)}
              >
                <div className="session-content">
                  <div className="session-title">{session.title}</div>
                  <div className="session-date">
                    {new Date(session.updatedAt).toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={(e) => deleteSession(session.id, e)}
                  className="delete-session-btn"
                  title="Delete conversation"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Mobile sidebar toggle */}
      <button
        className="mobile-sidebar-toggle"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Main Chat Area */}
      <div className="chat-main">
        <div className="chat-header">
          <div className="chat-header-content">
            <div>
              <h1>Cloud Solutions Architect</h1>
              <p>Ask me about cloud security, infrastructure, compliance, and scope planning</p>
            </div>
            <div className="chat-user-info">
              <span className="user-email">{user?.email}</span>
              <button onClick={logout} className="logout-btn" title="Sign out">
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>

        <div className="messages-container">
          {messages.length === 0 ? (
            <div className="welcome-message">
              <div className="welcome-icon">☁️</div>
              <h2>Welcome to Westbay Group Chat</h2>
              {!isAuthenticated && (
                <div className="demo-badge">
                  🎯 Demo Mode - Try 2 free questions
                </div>
              )}
              <p>I'm your Cloud Solutions Architect assistant specializing in cloud infrastructure, AI development, and custom tooling. I can help you with:</p>
              <div className="welcome-features">
                <div className="feature-item">🔒 Cloud Security Architecture</div>
                <div className="feature-item">🏗️ Infrastructure Engineering</div>
                <div className="feature-item">📈 Scalability & Auto-scaling</div>
                <div className="feature-item">👁️ Observability & Monitoring</div>
                <div className="feature-item">🤖 AI Development & Custom Tooling</div>
                <div className="feature-item">⚡ Custom Integrations & APIs</div>
                <div className="feature-item">📋 Compliance & Regulatory Requirements</div>
                <div className="feature-item">📊 Scope Planning & Project Estimation</div>
              </div>
              <p className="welcome-cta">Ask me anything to get started!</p>
            </div>
          ) : (
            messages.map((message) => (
              <div key={message.id} className={`message ${message.role}`}>
                <div className="message-avatar">
                  {message.role === 'user' ? '👤' : '🤖'}
                </div>
                <div className="message-content">
                  <div className="message-text">{message.content}</div>
                  <div className="message-time">
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))
          )}
          {isLoading && (
            <div className="message assistant">
              <div className="message-avatar">🤖</div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={sendMessage} className="input-form">
          <Textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about cloud security, AI development, custom tooling, or technical architecture..."
            className="message-input"
            rows={3}
            disabled={isLoading}
          />
          <button
            type="submit"
            className="send-button"
            disabled={!inputMessage.trim() || isLoading}
          >
            <Send size={20} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default Chat;